/**
 * WHOSE DATA THIS IS.
 *
 * There is no sign-in yet. Everything lives in one browser, so today this
 * returns a made-up id that browser invented for itself the first time it was
 * opened, and every job, customer and letterhead is written under it.
 *
 * That sounds like ceremony and it is not. When sign-in arrives, this one
 * function starts returning the account behind the session, and every call
 * site is already asking the right question. Without it, adding accounts means
 * finding every read and write in the app and threading an id through it,
 * while a season of saved jobs sits in the database belonging to nobody.
 *
 * THE RULE IT ENFORCES, EVEN NOW. A read is filtered by the account, not just
 * shown by it. Two people sharing a laptop is not a hypothetical in this
 * trade, and the difference between a list that hides other rows and a query
 * that cannot return them is the whole difference between a UI and a
 * boundary. Today that boundary sits in listProjects; later it moves into the
 * database, which is where it belongs, and nothing above it has to change.
 *
 * ONE LOGIN PER COMPANY, WITH ROOM FOR CREWS. An account is a company, and
 * right now it has exactly one person in it. Adding a second is a row in a
 * table nobody has had to build yet, rather than a rewrite of every table that
 * already exists.
 */

const KEY = 'lightsos.account.v1'

/** Not a secret and not an identity. Just something to file rows under. */
const mint = () => `local_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`

let cached: string | null = null
/**
 * True when the id in hand was invented just now rather than read back.
 *
 * It matters because the two live in different storage. The id is in
 * localStorage and the jobs are in IndexedDB, and a browser can lose one
 * without the other: an extension, a partial clear, a profile copied between
 * machines. When that happens the app mints a fresh id, the list filters every
 * job out, and a contractor opens a full database to an empty screen. That is
 * a data loss ticket for data that was never lost.
 *
 * So a freshly minted id is provisional until it has met the jobs. See
 * adoptFrom.
 */
let minted = false

/**
 * OPEN MODE: THE APP RUNS WITH NO SIGN IN, UNDER ONE NAMED ACCOUNT.
 *
 * Asked for as: make no login required for now, delete it. Deleting the door
 * on its own does not do that - it does the opposite. The account id is what
 * every saved job, customer and quote is keyed to, and with no session the id
 * falls back to an invented local_ one, so the app opens to an empty list and
 * the season looks lost.
 *
 * So the door goes and the account is named instead. Set VITE_OPEN_ACCOUNT to
 * the Supabase user id the work is already under and the app opens straight
 * into it. Unset, nothing changes and the sign in screen is back: this is one
 * environment variable to add and one to remove, not a deletion to undo.
 *
 * WHAT IT COSTS, and it is not small. Without a session Supabase refuses every
 * read and write, so the server sync stops. Jobs are saved in the browser that
 * drew them and go no further. One machine is fine. Two machines drift apart
 * and neither is wrong.
 */
export const OPEN_ACCOUNT: string =
  (typeof import.meta !== 'undefined' &&
    (import.meta as { env?: Record<string, string> }).env?.VITE_OPEN_ACCOUNT) ||
  ''

export function currentAccountId(): string {
  // Named account wins over anything this browser invented for itself.
  if (OPEN_ACCOUNT) return OPEN_ACCOUNT
  if (cached) return cached
  try {
    const saved = localStorage.getItem(KEY)
    if (saved) {
      cached = saved
      return saved
    }
    const made = mint()
    localStorage.setItem(KEY, made)
    cached = made
    minted = true
    return made
  } catch {
    /*
     * Private mode, or storage refused. A stable id for this page load is
     * still better than none: the session works, and nothing is written that
     * would survive to confuse a later one.
     */
    cached = mint()
    minted = true
    return cached
  }
}

/**
 * SIGNED IN: THE ACCOUNT IS NOW A REAL ONE.
 *
 * Everything above is scaffolding for a browser with nobody in it. The moment
 * there is a session, the account is the Supabase user id and the invented
 * local id stops being used - which is also why the invented ones are prefixed
 * local_, so a glance at a stored job says which era it came from.
 *
 * It is deliberately NOT written to the same key. Signing out has to leave the
 * browser as it was rather than adopting somebody's user id as this machine's
 * permanent identity.
 */
export function setAccountId(id: string) {
  cached = id
  // No longer provisional: a real account never inherits stray local jobs. The
  // jobs that were here get claimed deliberately, once, by the sync. See
  // lib/sync.ts.
  minted = false
}

/**
 * INHERIT THE JOBS THAT ARE ALREADY HERE.
 *
 * Handed the distinct account stamps found in this browser's saved jobs. If
 * the id we are holding was invented a moment ago, and everything in storage
 * is stamped with one other id, then that id is this browser's and ours is an
 * accident of a cleared key. Take theirs.
 *
 * It is deliberately narrow. Two different stamps in one browser means two
 * people really have used it, and guessing which one is worse than showing
 * nothing. An id that was read back from storage is never replaced, whatever
 * the jobs say.
 *
 * This weakens nothing that is currently a boundary. Until there is a sign in,
 * the stamp is bookkeeping, not security: two people sharing a laptop with no
 * password share the browser and everything in it. The real boundary arrives
 * with the account, and this line goes away when it does.
 */
export function adoptFrom(stamps: (string | undefined)[]): string {
  const id = currentAccountId()
  if (!minted) return id
  const others = [...new Set(stamps.filter((s): s is string => !!s && s !== id))]
  if (others.length !== 1) return id
  try {
    localStorage.setItem(KEY, others[0])
  } catch {
    /* it still applies for this session, which is the part that matters */
  }
  cached = others[0]
  minted = false
  return others[0]
}

/** For tests, and for the moment somebody signs out. */
export function forgetAccount() {
  cached = null
  minted = false
}
